import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle, Plus, ArrowLeft, Save, Trash2, Send, Eye, Search,
  Sparkles, Upload, RefreshCw, CheckCircle2, Clock, XCircle, FileText,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  category: "UTILITY",
  language: "ro",
  body: "",
  footer_note: "",
};

const CATEGORY_META = {
  UTILITY: { label: "Utilitar", color: "emerald", desc: "Recomandat. Livrare garantată (tranzacționale: AWB, comandă, RMA)" },
  MARKETING: { label: "Marketing", color: "amber", desc: "Necesită opt-in de la client. Promoții, oferte" },
  AUTHENTICATION: { label: "Autentificare", color: "sky", desc: "Pentru OTP-uri și coduri de verificare" },
};

const STATUS_META = {
  LOCAL:    { label: "Draft local",    color: "zinc",    icon: FileText },
  PENDING:  { label: "În aprobare",    color: "amber",   icon: Clock },
  APPROVED: { label: "Aprobat",        color: "emerald", icon: CheckCircle2 },
  REJECTED: { label: "Respins",        color: "red",     icon: XCircle },
  PAUSED:   { label: "Pauză Meta",     color: "orange",  icon: Clock },
  DISABLED: { label: "Dezactivat",     color: "zinc",    icon: XCircle },
};

function statusBadge(status) {
  const meta = STATUS_META[status] || STATUS_META.LOCAL;
  const Icon = meta.icon;
  return (
    <span data-testid={`wa-tpl-status-${status}`} className={`inline-flex items-center gap-1 rounded-full border border-${meta.color}-500/40 bg-${meta.color}-500/15 px-1.5 py-0.5 text-[10px] font-medium text-${meta.color}-200`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function catBadge(cat) {
  const meta = CATEGORY_META[cat] || CATEGORY_META.UTILITY;
  return (
    <span className={`inline-flex rounded-full border border-${meta.color}-500/40 bg-${meta.color}-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-${meta.color}-200`}>
      {meta.label}
    </span>
  );
}

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("ro-RO"); } catch { return d; }
}


export default function WhatsAppTemplates() {
  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState({ categories: {} });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, catRes] = await Promise.all([
        axios.get(`${API}/whatsapp-templates`),
        axios.get(`${API}/whatsapp-templates/catalog`),
      ]);
      setTemplates(tplRes.data?.items || []);
      setCatalog(catRes.data || { categories: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Eroare la încărcare");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh Meta status on page open (once)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/whatsapp-templates/refresh-all`);
        if (!cancelled) await loadAll();
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Ștergi șablonul "${name}"? Va fi șters și de la Meta.`)) return;
    try {
      await axios.delete(`${API}/whatsapp-templates/${id}`);
      toast.success("Șablon șters");
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/whatsapp-templates/refresh-all`);
      toast.success(`${r.data?.refreshed || 0} șabloane actualizate`);
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setRefreshing(false); }
  };

  const filtered = useMemo(
    () => templates.filter((t) => !search.trim() || t.name.toLowerCase().includes(search.toLowerCase()) || (t.meta_name || "").includes(search.toLowerCase())),
    [templates, search],
  );

  if (editing) {
    return (
      <EditorView
        initial={editing}
        catalog={catalog}
        onCancel={() => setEditing(null)}
        onSaved={async () => { await loadAll(); setEditing(null); }}
        onDelete={async (id, name) => { await handleDelete(id, name); setEditing(null); }}
      />
    );
  }

  return (
    <div data-testid="wa-templates-page">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="wa-tpl-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută după nume…"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 pl-7 pr-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-emerald-500/50 focus:outline-none"
          />
        </div>
        <button
          data-testid="wa-tpl-refresh"
          onClick={handleRefreshAll}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh status Meta
        </button>
        <button
          data-testid="wa-tpl-new-btn"
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-500/25"
        >
          <Plus className="h-3.5 w-3.5" /> Adaugă șablon
        </button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6 text-center text-xs text-zinc-500">Se încarcă…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/50 p-10 text-center">
          <MessageCircle className="mx-auto mb-3 h-8 w-8 text-zinc-600" />
          <div className="text-sm font-medium text-zinc-300">Niciun șablon WhatsApp</div>
          <div className="mt-1 text-[11px] text-zinc-500">Creează primul șablon (ex: „Comandă expediată&rdquo;) și trimite-l spre aprobare la Meta.</div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Nume</th>
                <th className="px-3 py-2 text-left">Meta name</th>
                <th className="px-3 py-2 text-left">Categorie</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Variabile</th>
                <th className="px-3 py-2 text-left">Actualizat</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} data-testid={`wa-tpl-row-${t.id}`} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                  <td className="px-3 py-2 text-zinc-100">
                    <div className="font-medium">{t.name}</div>
                    <div className="mt-0.5 line-clamp-1 text-[10px] text-zinc-500">{t.body}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{t.meta_name}</td>
                  <td className="px-3 py-2">{catBadge(t.category)}</td>
                  <td className="px-3 py-2">
                    {statusBadge(t.meta_status)}
                    {t.meta_rejected_reason && (
                      <div className="mt-1 text-[9px] text-red-400" title={t.meta_rejected_reason}>{t.meta_rejected_reason.slice(0, 40)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-zinc-400">{(t.variable_order || []).length}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500">{fmt(t.updated_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`wa-tpl-edit-${t.id}`}
                        onClick={() => setEditing(t)}
                        className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-300 hover:bg-zinc-800"
                      >Deschide</button>
                      <button
                        data-testid={`wa-tpl-del-${t.id}`}
                        onClick={() => handleDelete(t.id, t.name)}
                        className="rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 text-[10px] text-red-300 hover:bg-red-500/20"
                      ><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function EditorView({ initial, catalog, onCancel, onSaved, onDelete }) {
  const [form, setForm] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOrderId, setPreviewOrderId] = useState("");
  const [previewRmaId, setPreviewRmaId] = useState("");
  const [previewWarrantyId, setPreviewWarrantyId] = useState("");
  const [testTo, setTestTo] = useState("");
  const bodyRef = useState(null)[0];

  const isNew = !form.id;
  const status = form.meta_status || "LOCAL";
  const editable = !["PENDING", "APPROVED"].includes(status);

  const insertVar = (key) => {
    const insertion = "${" + key + "}";
    const el = document.getElementById("wa-tpl-body");
    if (el) {
      const start = el.selectionStart ?? form.body.length;
      const end = el.selectionEnd ?? form.body.length;
      const next = form.body.slice(0, start) + insertion + form.body.slice(end);
      setForm((f) => ({ ...f, body: next }));
      setTimeout(() => { el.focus(); el.setSelectionRange(start + insertion.length, start + insertion.length); }, 20);
    } else {
      setForm((f) => ({ ...f, body: (f.body || "") + insertion }));
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nume obligatoriu"); return; }
    if (!form.body.trim()) { toast.error("Textul mesajului e obligatoriu"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || "UTILITY",
        language: form.language || "ro",
        body: form.body,
        footer_note: form.footer_note || "",
      };
      const r = isNew
        ? await axios.post(`${API}/whatsapp-templates`, payload)
        : await axios.put(`${API}/whatsapp-templates/${form.id}`, payload);
      setForm(r.data);
      toast.success(isNew ? "Șablon creat" : "Șablon actualizat");
      if (isNew) await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const submitToMeta = async () => {
    if (!window.confirm("Trimit șablonul spre aprobare la Meta? După submit nu-l mai poți edita (doar șterge).")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/whatsapp-templates/${form.id}/submit`);
      setForm(r.data);
      toast.success("Șablon trimis la Meta! Status inițial: " + (r.data?.meta_status || "PENDING"));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.error?.message || detail?.message || e.message;
      toast.error(msg || "Eroare la submit Meta");
    } finally { setBusy(false); }
  };

  const refreshStatus = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/whatsapp-templates/${form.id}/refresh`);
      setForm(r.data);
      toast.success("Status actualizat: " + r.data.meta_status);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const doPreview = async () => {
    if (!form.id) { toast.error("Salvează șablonul întâi"); return; }
    try {
      const r = await axios.post(`${API}/whatsapp-templates/${form.id}/render`, {
        order_id: previewOrderId || undefined,
        rma_id: previewRmaId || undefined,
        warranty_id: previewWarrantyId || undefined,
      });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const testSend = async () => {
    if (!testTo.trim()) { toast.error("Introdu numărul destinatar (ex: 40751234567)"); return; }
    if (form.meta_status !== "APPROVED") { toast.error("Doar șabloane APPROVED pot fi trimise"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/whatsapp-templates/${form.id}/test-send`, {
        to: testTo.trim(),
        order_id: previewOrderId || undefined,
        rma_id: previewRmaId || undefined,
        warranty_id: previewWarrantyId || undefined,
      });
      toast.success("Test trimis către " + testTo);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.error?.message || e.message;
      toast.error(msg || "Eroare la trimitere test");
    } finally { setBusy(false); }
  };

  const bodyCharCount = (form.body || "").length;
  const footerCharCount = (form.footer_note || "").length;

  return (
    <div data-testid="wa-tpl-editor">
      <div className="mb-4 flex items-center justify-between gap-2">
        <button
          data-testid="wa-tpl-back"
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-100"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Înapoi la listă
        </button>
        <div className="flex items-center gap-2">
          {form.id && statusBadge(status)}
          {form.id && !editable && (
            <button
              data-testid="wa-tpl-refresh-status"
              onClick={refreshStatus}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Refresh
            </button>
          )}
          {form.id && (
            <button
              data-testid="wa-tpl-editor-del"
              onClick={() => onDelete(form.id, form.name)}
              className="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 text-[10px] text-red-300 hover:bg-red-500/20"
            >
              <Trash2 className="h-3 w-3" /> Șterge
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-3">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
            <label className="mb-1 block text-[10px] font-medium text-zinc-400">Nume șablon (afișat în listă)</label>
            <input
              data-testid="wa-tpl-name"
              disabled={!editable}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="ex: Comandă expediată"
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-100 outline-none focus:border-emerald-500 disabled:opacity-60"
            />
            {form.meta_name && (
              <div className="mt-1 text-[10px] text-zinc-500">Nume tehnic Meta: <span className="font-mono text-zinc-400">{form.meta_name}</span></div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <label className="mb-1 block text-[10px] font-medium text-zinc-400">Categorie</label>
              <select
                data-testid="wa-tpl-category"
                disabled={!editable}
                value={form.category || "UTILITY"}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500 disabled:opacity-60"
              >
                <option value="UTILITY">Utility (recomandat)</option>
                <option value="MARKETING">Marketing</option>
                <option value="AUTHENTICATION">Authentication</option>
              </select>
              <p className="mt-1 text-[10px] text-zinc-500">{CATEGORY_META[form.category || "UTILITY"].desc}</p>
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <label className="mb-1 block text-[10px] font-medium text-zinc-400">Limbă</label>
              <select
                data-testid="wa-tpl-lang"
                disabled={!editable}
                value={form.language || "ro"}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500 disabled:opacity-60"
              >
                <option value="ro">Română (ro)</option>
              </select>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-[10px] font-medium text-zinc-400">Textul mesajului</label>
              <span className={`text-[10px] ${bodyCharCount > 1024 ? "text-red-400" : "text-zinc-500"}`}>{bodyCharCount}/1024</span>
            </div>
            <textarea
              id="wa-tpl-body"
              data-testid="wa-tpl-body"
              disabled={!editable}
              rows={9}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Salut ${client.name}!&#10;&#10;Comanda ta #${order.display_id} a fost expediată prin ${courier.name}.&#10;AWB: ${order.awb_id}&#10;Tracking: ${order.awb_tracking_url}&#10;&#10;Mulțumim!&#10;Bragus.ro"
              className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-xs text-zinc-100 outline-none focus:border-emerald-500 disabled:opacity-60"
              maxLength={1024}
            />
            <p className="mt-1 text-[10px] text-zinc-500">Folosește <code className="text-emerald-300">${"${variabila}"}</code> pentru inserții dinamice. Panou variabile în dreapta.</p>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-[10px] font-medium text-zinc-400">Footer (opțional)</label>
              <span className={`text-[10px] ${footerCharCount > 60 ? "text-red-400" : "text-zinc-500"}`}>{footerCharCount}/60</span>
            </div>
            <input
              data-testid="wa-tpl-footer"
              disabled={!editable}
              value={form.footer_note || ""}
              onChange={(e) => setForm({ ...form, footer_note: e.target.value })}
              placeholder="ex: Bragus.ro — Magazin online"
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-100 outline-none focus:border-emerald-500 disabled:opacity-60"
              maxLength={60}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {editable && (
              <button
                data-testid="wa-tpl-save"
                onClick={save}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" /> {isNew ? "Creează" : "Salvează modificările"}
              </button>
            )}
            {form.id && editable && (
              <button
                data-testid="wa-tpl-submit"
                onClick={submitToMeta}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/50 bg-sky-500/15 px-3 py-1.5 text-xs font-medium text-sky-100 hover:bg-sky-500/25 disabled:opacity-50"
              >
                <Upload className="h-3.5 w-3.5" /> Trimite spre aprobare Meta
              </button>
            )}
            {form.id && (
              <button
                data-testid="wa-tpl-preview"
                onClick={doPreview}
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"
              >
                <Eye className="h-3.5 w-3.5" /> Preview
              </button>
            )}
          </div>

          {/* Preview inputs */}
          {form.id && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
              <div className="mb-2 text-[10px] font-medium text-zinc-400">Date pentru preview & test</div>
              <div className="grid grid-cols-3 gap-2">
                <input
                  data-testid="wa-tpl-preview-order"
                  value={previewOrderId}
                  onChange={(e) => setPreviewOrderId(e.target.value)}
                  placeholder="ID comandă (opțional)"
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-[11px] text-zinc-200"
                />
                <input
                  data-testid="wa-tpl-preview-rma"
                  value={previewRmaId}
                  onChange={(e) => setPreviewRmaId(e.target.value)}
                  placeholder="ID retur RMA (opțional)"
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-[11px] text-zinc-200"
                />
                <input
                  data-testid="wa-tpl-preview-warranty"
                  value={previewWarrantyId}
                  onChange={(e) => setPreviewWarrantyId(e.target.value)}
                  placeholder="ID garanție (opțional)"
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-[11px] text-zinc-200"
                />
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="mb-1 text-[10px] font-semibold uppercase text-emerald-300">Preview mesaj real</div>
              <div className="whitespace-pre-wrap rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-xs text-zinc-100">{preview.body}</div>
              {preview.footer && (
                <div className="mt-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-[10px] text-zinc-400">{preview.footer}</div>
              )}
              {preview.params?.length > 0 && (
                <div className="mt-2 text-[10px] text-zinc-500">Params ordonați ce se trimit la Meta: <span className="font-mono">[{preview.params.map((p) => JSON.stringify(p)).join(", ")}]</span></div>
              )}
            </div>
          )}

          {/* Test send */}
          {form.meta_status === "APPROVED" && (
            <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase text-sky-200">Trimite test</div>
              <div className="flex gap-2">
                <input
                  data-testid="wa-tpl-test-to"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="40751234567 (fără +)"
                  className="flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100"
                />
                <button
                  data-testid="wa-tpl-test-send"
                  onClick={testSend}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-md bg-sky-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-sky-400 disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" /> Trimite
                </button>
              </div>
              <p className="mt-1 text-[10px] text-zinc-500">Se completează variabilele cu datele din câmpurile de preview de sus (ID comandă, retur, garanție).</p>
            </div>
          )}
        </div>

        {/* Right: variable picker */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 rounded-lg border border-zinc-800 bg-zinc-900 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-zinc-100">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" /> Variabile disponibile
            </div>
            <div className="max-h-[70vh] overflow-y-auto pr-1">
              {Object.entries(catalog.categories || {}).map(([cat, vars]) => (
                <div key={cat} className="mb-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{cat}</div>
                  <div className="flex flex-wrap gap-1">
                    {vars.map((v) => (
                      <button
                        key={v.key}
                        type="button"
                        disabled={!editable}
                        onClick={() => insertVar(v.key)}
                        title={"${" + v.key + "}"}
                        data-testid={`wa-tpl-var-${v.key}`}
                        className="rounded border border-zinc-800 bg-zinc-950 px-1.5 py-0.5 text-[10px] text-zinc-300 hover:border-emerald-500/50 hover:text-emerald-200 disabled:opacity-40"
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
